import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { TranslationCountryLanding } from "@/content/translation-corpus/countries";

function faqsEn(c: TranslationCountryLanding) {
  return [
    { q: `Which translation system does ${c.countryEn} require?`, a: `${c.systemEn} Registry to verify: ${c.registryEn}.` },
    { q: "What is the correct order of steps?", a: c.routeEn },
    { q: "What is it used for?", a: c.useCaseEn },
    { q: "Why are translations rejected?", a: c.pitfallsEn.join(" · ") },
    { q: "How long does it take?", a: `${c.turnaroundEn} This excludes the receiving authority's own queue.` },
    {
      q: `Is ${c.countryEn} a party to the Hague Apostille Convention?`,
      a: c.hague
        ? `${c.countryEn} is a Hague party, but an apostille can only be used between the two countries once the convention enters into force for Thailand on 28 February 2027. Until then Thai documents follow MFA legalisation plus destination-embassy attestation. Check current status at hcch.net.`
        : `No. Documents therefore continue on the legalisation route through the Department of Consular Affairs and the ${c.countryEn} embassy, even after 2027. Check status at hcch.net.`,
    },
    {
      q: "How do translation, certification of accuracy, signature attestation and legalisation differ?",
      a: "They are four separate layers: (1) translating the content, (2) the translator's certificate of accuracy, (3) attestation of a signatory's signature by a lawyer authorised to certify signatures and documents, and (4) legalisation or apostille certifying the seals for cross-border use. The first three cannot replace the fourth.",
    },
    {
      q: "Should I translate before or after legalisation?",
      a: "If the destination requires a translator based in that country, legalise in Thailand first: the translator must render every stamp inside the translation. If the destination accepts a translation prepared in Thailand, translate and certify first, then legalise the translation.",
    },
    {
      q: "Can you guarantee the documents will be accepted?",
      a: "Acceptance is at the receiving authority's discretion, so we do not guarantee outcomes. We confirm the required certification system and document age before starting, and redo our translation work free of charge if the error is on our side.",
    },
  ];
}

export const Route = createFileRoute("/en/translation/country/$country")({
  loader: async ({ params }: { params: { country: string } }) => {
    const { TRANSLATION_COUNTRY_LANDINGS, getTranslationCountryLanding } = await import("@/content/translation-corpus/countries");
    const c = getTranslationCountryLanding(params.country);
    if (!c) throw notFound();
    return { c, siblings: TRANSLATION_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => ({ slug: x.slug, label: x.countryEn })) };
  },
  component: TranslationCountryPageEN,
  head: ({ params, loaderData }: { params: { country: string }; loaderData?: { c: TranslationCountryLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "Destination not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    const title = `Certified Translation for ${c.countryEn} — Systems & Legalisation`;
    const description = `Thai documents for ${c.countryEn}: which translation system is accepted, the correct legalisation order, common rejection causes and realistic timelines.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/translation/country/${params.country}`,
        type: "article",
        alternates: [{ lang: "th", path: `/translation/country/${params.country}` }],
        keywords: [
          `certified translation ${c.countryEn}`,
          `sworn translator ${c.countryEn}`,
          `Thai document legalisation ${c.countryEn}`,
          "MFA legalisation Thailand",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Certified Translation", url: `${site}/en/translation` },
              { name: c.countryEn, url: `${site}/en/translation/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(c))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/translation/country/${params.country}`)) },
      ],
    };
  },
});

function TranslationCountryPageEN() {
  const { c, siblings } = Route.useLoaderData();
  const faqs = faqsEn(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/translation/faq" className="hover:underline">Certified Translation</a> <span>/</span>{" "}
          <span>{c.countryEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">Certified Translation of Thai Documents for {c.countryEn}</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          Accepted system at destination: <strong>{c.systemEn}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/translation/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Translation FAQ</a>
          <a href={`/translation/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Translator system and verifiable register</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.systemEn}</p>
          <p className="mt-3 text-sm text-muted-foreground"><strong>Register / authority:</strong> {c.registryEn}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            <strong>Hague Convention status:</strong>{" "}
            {c.hague ? "Party to the convention (verify at hcch.net)" : "Not a party — legalisation and embassy attestation remain required"}
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Correct order of steps</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.routeEn}</p>
          <p className="mt-3 text-sm text-muted-foreground"><strong>Timeline:</strong> {c.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Typical use cases</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.useCaseEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Why translations get rejected</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — Thai documents for {c.countryEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other destination countries</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/en/translation/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Information current as of August 2026 — sources for this page: {c.sources.join(" · ")} · Hague status via hcch.net.
          Destination requirements change; verify with the receiving authority before you file.
        </p>
      </main>
      <SiteFooter />
    </>
  );
}
